import { type ShallowRef, computed, shallowRef } from 'vue';
import { addMinutes, isAfter, parse } from 'date-fns';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.js';
import type { OpenLoan } from '@/modules/common/models';

// Shared singleton state — single interval for the entire app.
// Provides time-based reactivity so computed properties relying on
// isFeatureEnabled() are recomputed as time changes (e.g. market-closed timeframes).
const currentTimeUTC: ShallowRef<Date> = shallowRef(new Date());

const localTimezone = computed(() => moment(currentTimeUTC.value).tz(moment.tz.guess()).zoneAbbr());

// Start the minute-aligned interval on module evaluation.
// First update is aligned to the next whole minute, then repeats every 60s.
function startTimeInterval(): void {
  const interval = 60 * 1000;
  const timeToNextMin = (60 - new Date().getSeconds()) * 1000;

  setTimeout(() => {
    currentTimeUTC.value = new Date();
    setInterval(() => {
      currentTimeUTC.value = new Date();
    }, interval);
  }, timeToNextMin);
}

startTimeInterval();

if (import.meta.env.DEV) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  w.__timeRef = currentTimeUTC;
  w.__setTime = (iso: string) => {
    currentTimeUTC.value = new Date(iso);
  };
}

export function useCurrentTime(): {
  currentTimeUTC: ShallowRef<Date>;
  localTimezone: ReturnType<typeof computed<string>>;
} {
  return { currentTimeUTC, localTimezone };
}

const cutOffFromFormat = 'HH:mm';

export function isAfterCutOffTime(loan: OpenLoan, now: Date, timezoneOffset?: string): boolean {
  if (!loan.counterparty.cutOffTime) {
    return false;
  }

  const cutOff = parse(
    `${loan.counterparty.cutOffTime} ${timezoneOffset ?? '+0000'}`,
    `${cutOffFromFormat} X`,
    now
  );

  return isAfter(addMinutes(now, 1), cutOff);
}
